import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import size from 'lodash/size';

import { getDiscounts } from 'services/api/admin-discounts';
import { saveToCSV, formatTimestamp, CSV_MODAL_TYPES } from 'utils/admin-csv';
import {
  AdminSelect,
  AdminTable,
  AdminFormRow,
  AdminInput,
} from 'shared-components';
import PageContainer from 'shared-components/PageContainer/PageContainer';
import Button, { BUTTON_TYPES } from 'shared-components/Button/Button';
import CSVModal from 'shared-components/CSVModal/CSVModal';
import { Toast, showSuccessToast } from 'shared-components/BaseToast/Toast';
import discountsModel from './discountsModel';
import styles from './DiscountsPage.module.css';

const EMPTY_COPY = {
  noCampaignOrAccountCode: {
    icon: 'fal fa-search',
    title: 'No Campaign or Account Code(s) Selected',
    description: 'Select your search criteria to see results.',
  },
  noResults: {
    icon: 'fal fa-ghost dark',
    title: 'No Results Match Your Search',
    description: 'Please clear your search and try again.',
  },
};

const DiscountsPage = ({ campaigns }) => {
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [accountCodes, setAccountCodes] = useState('');
  const [discounts, setDiscounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [emptyCopy, setEmptyCopy] = useState({});
  const [modalOpen, setModalOpen] = useState(null);

  useEffect(() => {
    if (!selectedCampaign || !accountCodes) {
      setEmptyCopy(EMPTY_COPY.noCampaignOrAccountCode);
    }
  }, [selectedCampaign, accountCodes]);

  const handleCancel = () => {
    setSelectedCampaign('');
    setAccountCodes('');
    setDiscounts([]);
  };

  const fetchDiscounts = async () => {
    setIsLoading(true);
    const response = await getDiscounts(selectedCampaign, accountCodes);
    const formatted = formatTimestamp(response, 'modifiedAt');
    setDiscounts(formatted);
    if (!size(formatted)) {
      setEmptyCopy(EMPTY_COPY.noResults);
    }
    setIsLoading(false);
  };

  const submitForm = (e) => {
    e.preventDefault();
    fetchDiscounts();
  };

  const handleUploadSuccess = ({ message }) => showSuccessToast(message);

  const buttonsDisabled = !accountCodes || !selectedCampaign;

  return (
    <>
      <PageContainer>
        <div className={styles.container}>
          <h2>Discounts</h2>
          <form onSubmit={submitForm}>
            <AdminFormRow>
              <AdminSelect
                name="campaign"
                label="Selected Campaign"
                list={campaigns}
                selected={selectedCampaign}
                selectLabel="Select a Campaign"
                handleChange={(e) => setSelectedCampaign(e.target.value)}
              />
              <AdminInput
                label="Account Code(s)"
                placeholder="Separate multiple account codes by commas"
                type="text"
                style={{ width: '486px' }}
                onChange={(e) => setAccountCodes(e.target.value)}
                value={accountCodes}
              />
            </AdminFormRow>
            <div>
              <Button
                buttonType={BUTTON_TYPES.PRIMARY}
                style={{ width: '120px' }}
                type="submit"
                disabled={buttonsDisabled}
              >
                Search
              </Button>
              {!buttonsDisabled && (
                <Button
                  style={{ width: '120px' }}
                  buttonType={BUTTON_TYPES.CANCEL}
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
              )}
            </div>
          </form>

          <hr />
          <div>
            <div className={styles.tableHeader}>
              <h3>Sale Discounts</h3>
              <div>
                <Button
                  buttonType={BUTTON_TYPES.PRIMARY_INVERSE}
                  className={styles.iconButton}
                  disabled={!discounts.length}
                  onClick={() =>
                    saveToCSV(
                      discountsModel,
                      discounts,
                      `discounts-campaign${selectedCampaign}`
                    )
                  }
                >
                  <i className="far fa-arrow-to-bottom" />
                </Button>
                <Button
                  buttonType={BUTTON_TYPES.PRIMARY}
                  style={{ width: '180px' }}
                  onClick={() => setModalOpen(CSV_MODAL_TYPES.editDiscounts)}
                >
                  Upload CSV
                </Button>
              </div>
            </div>
            <AdminTable
              model={discountsModel}
              rows={discounts}
              idFieldName="campaignId"
              isLoading={isLoading}
              emptyIconClassName={emptyCopy.icon}
              emptyTitle={emptyCopy.title}
              emptyDescription={emptyCopy.description}
            />
          </div>
        </div>
      </PageContainer>
      <CSVModal
        type={modalOpen}
        title="CSV Upload"
        onClickClose={() => setModalOpen(null)}
        updateData={fetchDiscounts}
        onUploadSuccess={handleUploadSuccess}
      />
      <Toast />
    </>
  );
};

DiscountsPage.propTypes = {
  campaigns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

const mapStateToProps = (state) => ({
  campaigns: state.adminState.campaigns,
});

export default connect(mapStateToProps, {})(DiscountsPage);
